import { oktaAuth } from "../../auth/services/oktaAuth";
import { getFrontendApiResource, isQuietFrontendRequest } from "../utils/frontend-logs";
import { log, logV, logE } from "./debug-logger";
import axios, { AxiosResponse } from "axios";

interface ApiResponse<T> {
  data: T;
}

export const apiUrl = process.env.REACT_APP_API_URL ?? "default-api-url";

interface QueryParams {
  page: number;
  pageSize: number;
}

export const api = axios.create();
const requestStarts = new WeakMap<object, number>();

let cachedToken: string;

// Set the AUTH token for any request
api.interceptors.request.use((config) => {
  let accessToken = oktaAuth.getAccessToken();
  // Workaround for missing access tokeon on Tech Form Submit.
  // Currently it's not clear why the token is missing when extuserTechFormSubmission is invoked.
  // To be able to proceed with development I simply cached the token from the initial EP (Adrian).
  // Will be looking for a proper fix.
  if (accessToken) {
    cachedToken = accessToken;
  }
  if (!accessToken && cachedToken) {
    accessToken = cachedToken;
  }
  config.headers.Authorization = accessToken ? `Bearer ${accessToken}` : "";
  requestStarts.set(config, Date.now());
  (isQuietFrontendRequest(config.url) ? logV : log)("REPO - request - start", {
    method: config.method?.toUpperCase(),
    resource: getFrontendApiResource(config.url),
  });
  return config;
});

api.interceptors.response.use(
  (response) => {
    const startedAt = requestStarts.get(response.config);
    (isQuietFrontendRequest(response.config.url) ? logV : log)("REPO - request - response", {
      resource: getFrontendApiResource(response.config.url),
      status: response.status,
      ms: startedAt === undefined ? undefined : Date.now() - startedAt,
    });
    requestStarts.delete(response.config);
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.config) {
      const { config, response } = error;
      const startedAt = requestStarts.get(config);
      if (!axios.isCancel(error))
        (isQuietFrontendRequest(config.url) ? logV : log)("REPO - request - response", {
          resource: getFrontendApiResource(config.url),
          status: response?.status ?? "no-response",
          ms: startedAt === undefined ? undefined : Date.now() - startedAt,
        });
      requestStarts.delete(config);
    }
    handleApiError(error);
    return Promise.reject(error);
  },
);

export const fetchDataWithParams = async (endpoint: string, params?: any): Promise<ApiResponse<any>> => {
  try {
    const response: AxiosResponse = await api.get(`${apiUrl}/${endpoint}`, {
      params,
    });

    return { data: response.data };
  } catch (error) {
    throw error;
  }
};

export const postData = async (
  endpoint: string,
  data: any,
  params: QueryParams | string | undefined = undefined,
): Promise<ApiResponse<any>> => {
  try {
    const response: AxiosResponse = await api.post(`${apiUrl}/${endpoint}`, data, {
      params,
    });

    return { data: response.data };
  } catch (error) {
    throw error;
  }
};

const handleApiError = (error: unknown) => {
  if (axios.isCancel(error)) return;
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 404) {
      // Optional-resource reads use 404 as an empty state; their feature owns the outcome.
      logV("REPO - handleApiError - unavailable", { resource: getFrontendApiResource(error.config?.url), status });
      return;
    }
    if (status === 400 || status === 409 || status === 422) {
      log("REPO - handleApiError - rejected", { resource: getFrontendApiResource(error.config?.url), status });
      return;
    }
    logE(error, { operation: "REPO - handleApiError - failed", resource: getFrontendApiResource(error.config?.url) });
  } else {
    logE(error, { operation: "REPO - handleApiError - unexpected failure" });
  }
};
